'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

export default function Row9TeamAndCulture() {
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <AnniversariesAndBirthdays />
      <CustomerQuoteOfTheWeek />
      <CompetitorWatch />
      <RecentTeamWins />
    </section>
  );
}

// ── 5. Anniversary & Birthday Tracker ──────────────────────────────────────

type Occasion = 'anniversary' | 'birthday';

interface Celebration {
  name: string;
  occasion: Occasion;
  detail: string;  // e.g. "2-year work anniversary"
  dateLabel: string;
}

const CELEBRATIONS: Celebration[] = [];

function initialsOf(full: string): string {
  return full
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function AnniversariesAndBirthdays() {
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="GiftIcon" size={15} style={{ color: '#8B5CF6' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Upcoming
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Next 30 days</p>
        </div>
      </div>
      <div className="space-y-2">
        {CELEBRATIONS.map((c) => (
          <div
            key={c.name + c.dateLabel}
            className="flex items-center gap-2 rounded-lg px-2.5 py-2"
            style={{ border: '1px solid #F3F4F6' }}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}
            >
              {initialsOf(c.name)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-gray-800 truncate">{c.name}</p>
              <p className="text-[10px] text-gray-500 truncate">{c.detail}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-base leading-none">{c.occasion === 'anniversary' ? '🎉' : '🎂'}</span>
              <span className="text-[10px] font-semibold text-gray-600">{c.dateLabel}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 6. Customer Quote of the Week ──────────────────────────────────────────

const QUOTE = {
  text: '',
  name: '—',
  city: '—',
  rating: 0,
  dateLabel: '—',
};

function CustomerQuoteOfTheWeek() {
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="ChatBubbleBottomCenterTextIcon" size={15} style={{ color: '#10B981' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Customer Quote of the Week
            </h3>
          </div>
        </div>
      </div>
      <div
        className="rounded-lg p-4 mb-3 relative"
        style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.18)' }}
      >
        <span
          className="absolute top-1 left-2 text-3xl leading-none select-none pointer-events-none"
          style={{ color: 'rgba(16,185,129,0.35)', fontFamily: 'Georgia, serif' }}
          aria-hidden
        >
          “
        </span>
        <p
          className="text-[13px] italic leading-relaxed text-gray-700 pl-4 pr-2"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          {QUOTE.text}
        </p>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-gray-800 truncate">{QUOTE.name}</p>
          <p className="text-[10px] text-gray-500 truncate">{QUOTE.city} · {QUOTE.dateLabel}</p>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0" style={{ color: '#F59E0B' }}>
          {Array.from({ length: QUOTE.rating }).map((_, i) => (
            <Icon key={i} name="StarIcon" size={11} />
          ))}
        </div>
      </div>
      <a
        href="#"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(16,185,129,0.10)', color: '#047857' }}
      >
        View all reviews
        <Icon name="ArrowRightIcon" size={10} />
      </a>
    </div>
  );
}

// ── 7. Competitor Watch ────────────────────────────────────────────────────

interface Competitor {
  name: string;
  markets: string;
  note: string;
  noteDate: string;
}

const COMPETITORS: Competitor[] = [];

function CompetitorWatch() {
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="EyeIcon" size={15} style={{ color: '#EF4444' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Competitor Watch
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{COMPETITORS.length} tracked</p>
        </div>
      </div>
      <div className="space-y-2">
        {COMPETITORS.map((c) => (
          <div key={c.name} className="rounded-lg px-2.5 py-2" style={{ border: '1px solid #F3F4F6' }}>
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <p className="text-[11px] font-semibold text-gray-800 truncate">{c.name}</p>
              <span className="text-[10px] text-gray-400 flex-shrink-0">{c.noteDate}</span>
            </div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">{c.markets}</p>
            <p className="text-[11px] text-gray-700 leading-snug">{c.note}</p>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(239,68,68,0.10)', color: '#B91C1C' }}
        suppressHydrationWarning
      >
        + Add competitor
      </button>
    </div>
  );
}

// ── 8. Recent Team Wins ────────────────────────────────────────────────────

type WinCategory = 'contract' | 'review' | 'milestone' | 'certification' | 'press';

interface Win {
  category: WinCategory;
  brief: string;
  dateLabel: string;
}

const WINS: Win[] = [];

function winIcon(c: WinCategory): { name: string; color: string; bg: string } {
  if (c === 'contract')      return { name: 'BriefcaseIcon',  color: '#033494', bg: 'rgba(3,52,148,0.10)' };
  if (c === 'review')        return { name: 'StarIcon',       color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' };
  if (c === 'certification') return { name: 'AcademicCapIcon', color: '#8B5CF6', bg: 'rgba(139,92,246,0.10)' };
  if (c === 'press')         return { name: 'NewspaperIcon',  color: '#07B0E9', bg: 'rgba(7,176,233,0.10)' };
  return                            { name: 'TrophyIcon',     color: '#10B981', bg: 'rgba(16,185,129,0.10)' };
}

function RecentTeamWins() {
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="TrophyIcon" size={15} style={{ color: '#10B981' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Recent Wins
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Last 30 days</p>
        </div>
      </div>
      <div className="space-y-2">
        {WINS.map((w, i) => {
          const ic = winIcon(w.category);
          return (
            <div key={i} className="flex items-start gap-2 rounded-lg px-2.5 py-2" style={{ border: '1px solid #F3F4F6' }}>
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: ic.bg }}
              >
                <Icon name={ic.name} size={13} style={{ color: ic.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-gray-700 leading-snug">{w.brief}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{w.dateLabel}</p>
              </div>
            </div>
          );
        })}
      </div>
      <a
        href="#"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(16,185,129,0.10)', color: '#047857' }}
      >
        Share to Slack
        <Icon name="ArrowRightIcon" size={10} />
      </a>
    </div>
  );
}
